/**
 * Phase 16 Plan 16-03 — API client for the 11 `/api/workorders` endpoints from Plan 16-01.
 *
 * Each function:
 *  - Throws `ApiError` (via `toApiError`) on non-2xx.
 *  - Returns the typed response object on success.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type { PaginatedResult } from '@/types/roles';
import type {
  AddRemoveMachinesRequest,
  AddRemoveSitesRequest,
  CancelWorkOrderRequest,
  CompleteWorkOrderResponse,
  CreateWorkOrderRequest,
  UpdateWorkOrderRequest,
  WorkOrderAuditListQuery,
  WorkOrderAuditLogItem,
  WorkOrderDetail,
  WorkOrderListItem,
  WorkOrderListQuery,
} from '@/types/work-orders';

function buildListUrl(query: WorkOrderListQuery): string {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q !== '') params.set('q', query.q);
  if (query.clientId !== undefined) params.set('clientId', String(query.clientId));
  if (query.siteId !== undefined) params.set('siteId', String(query.siteId));
  if (query.status !== undefined) params.set('status', query.status);
  if (query.startDateFrom !== undefined && query.startDateFrom !== '')
    params.set('startDateFrom', query.startDateFrom);
  if (query.startDateTo !== undefined && query.startDateTo !== '')
    params.set('startDateTo', query.startDateTo);
  params.set('page', String(query.page ?? 1));
  params.set('pageSize', String(query.pageSize ?? 20));
  return `/api/workorders?${params.toString()}`;
}

export async function listWorkOrdersRequest(
  query: WorkOrderListQuery = {},
): Promise<PaginatedResult<WorkOrderListItem>> {
  try {
    const { data } = await api.get<PaginatedResult<WorkOrderListItem>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getWorkOrderRequest(id: number): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.get<WorkOrderDetail>(`/api/workorders/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function createWorkOrderRequest(body: CreateWorkOrderRequest): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>('/api/workorders', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function updateWorkOrderRequest(
  id: number,
  body: UpdateWorkOrderRequest,
): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.put<WorkOrderDetail>(`/api/workorders/${String(id)}`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function addMachinesRequest(
  id: number,
  body: AddRemoveMachinesRequest,
): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>(
      `/api/workorders/${String(id)}/machines/add`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function removeMachinesRequest(
  id: number,
  body: AddRemoveMachinesRequest,
): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>(
      `/api/workorders/${String(id)}/machines/remove`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function activateWorkOrderRequest(id: number): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>(`/api/workorders/${String(id)}/activate`, null);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function completeWorkOrderRequest(id: number): Promise<CompleteWorkOrderResponse> {
  try {
    const { data } = await api.post<CompleteWorkOrderResponse>(
      `/api/workorders/${String(id)}/complete`,
      null,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function reopenWorkOrderRequest(id: number): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>(
      `/api/workorders/${String(id)}/reopen`,
      null,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function cancelWorkOrderRequest(
  id: number,
  body: CancelWorkOrderRequest,
): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>(
      `/api/workorders/${String(id)}/cancel`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function addSitesRequest(
  id: number,
  body: AddRemoveSitesRequest,
): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>(
      `/api/workorders/${String(id)}/sites/add`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function removeSitesRequest(
  id: number,
  body: AddRemoveSitesRequest,
): Promise<WorkOrderDetail> {
  try {
    const { data } = await api.post<WorkOrderDetail>(
      `/api/workorders/${String(id)}/sites/remove`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

function buildAuditUrl(id: number, query: WorkOrderAuditListQuery): string {
  const params = new URLSearchParams();
  params.set('page', String(query.page ?? 1));
  params.set('pageSize', String(query.pageSize ?? 20));
  return `/api/workorders/${String(id)}/audit?${params.toString()}`;
}

export async function getWorkOrderAuditRequest(
  id: number,
  query: WorkOrderAuditListQuery = {},
): Promise<PaginatedResult<WorkOrderAuditLogItem>> {
  try {
    const { data } = await api.get<PaginatedResult<WorkOrderAuditLogItem>>(
      buildAuditUrl(id, query),
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
